import React, { useState, useEffect } from 'react';
import { conversationService, ConversationSummary, ConversationData } from '../../../../services/conversationService';
import { Message } from '../../types';
import { WorkspaceCard } from '../Workspace/WorkspacePanel';
import ReactMarkdown from 'react-markdown';

interface ConversationHistoryProps {
  userEmail: string;
  onResumeConversation?: (sessionData: ConversationData) => void;
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ userEmail, onResumeConversation }) => {
  const [sessions, setSessions] = useState<ConversationSummary[]>([]);
  const [selectedSession, setSelectedSession] = useState<ConversationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, [userEmail]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await conversationService.getConversations(userEmail, { limit: 20 });
      setSessions(response.sessions);
    } catch (err) {
      setError('Failed to load conversation history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFullSession = async (sessionId: string) => {
    try {
      setLoadingSession(true);
      const fullSession = await conversationService.getConversation(userEmail, sessionId);
      setSelectedSession(fullSession);
    } catch (err) {
      setError('Failed to load conversation details');
      console.error(err);
    } finally {
      setLoadingSession(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + 
        ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-gray-600">Loading conversation history...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-600">{error}</div>;
  }

  if (sessions.length === 0) {
    return (
      <div className="p-8 text-center text-gray-600">
        <p className="text-lg mb-2">No conversations yet</p>
        <p className="text-sm">Your Talk to Tally conversation history will appear here</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {!selectedSession ? (
        <div>
          <h3 className="text-lg font-medium mb-4">Your Conversation History</h3>
          <div className="space-y-2">
            {sessions.map((session) => (
              <div
                key={session.sessionId}
                onClick={async () => {
                  if (onResumeConversation) {
                    try {
                      setLoadingSessionId(session.sessionId);
                      // Load full session data first
                      const fullSession = await conversationService.getConversation(userEmail, session.sessionId);
                      onResumeConversation(fullSession);
                    } catch (err) {
                      console.error('Failed to load conversation:', err);
                      setError('Failed to load conversation');
                    } finally {
                      setLoadingSessionId(null);
                    }
                  } else {
                    // Fallback to showing details if no resume handler
                    loadFullSession(session.sessionId);
                  }
                }}
                className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors relative ${
                  loadingSessionId === session.sessionId ? 'opacity-50' : ''
                }`}
              >
                {loadingSessionId === session.sessionId && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
                    <div className="text-gray-600">Loading conversation...</div>
                  </div>
                )}
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium">{(session as any).title || session.topic || 'General Practice'}</div>
                    {(session as any).description && (
                      <div className="text-sm text-gray-700 mt-1">{(session as any).description}</div>
                    )}
                    <div className="text-sm text-gray-600 mt-1">{formatDate(session.createdAt)}</div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div>{formatDuration(session.duration)}</div>
                    <div>{session.messageCount} messages</div>
                  </div>
                </div>
                <div className="flex gap-4 text-sm text-gray-600">
                  {session.hasAssessment && (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                      ✓ Assessment
                    </span>
                  )}
                  {session.workspaceItemCount > 0 && (
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {session.workspaceItemCount} workspace {session.workspaceItemCount === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => setSelectedSession(null)}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Back to history
          </button>
          
          {loadingSession ? (
            <div className="text-center text-gray-600">Loading conversation...</div>
          ) : (
            <div>
              <div className="mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium">{selectedSession.title || selectedSession.topic || 'General Practice'}</h3>
                    {selectedSession.description && (
                      <p className="text-gray-700 mt-1">{selectedSession.description}</p>
                    )}
                    <div className="text-sm text-gray-600 mt-2">
                      {formatDate(selectedSession.createdAt)} • {formatDuration(selectedSession.duration)} • {selectedSession.transcript.length} messages
                    </div>
                  </div>
                  {onResumeConversation && (
                    <button
                      onClick={() => onResumeConversation(selectedSession)}
                      className="px-4 py-2 bg-talaera-blue text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Resume Conversation
                    </button>
                  )}
                </div>
              </div>

              {/* Transcript */}
              <div className="mb-6">
                <h4 className="font-medium mb-3">Conversation</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto border rounded-lg p-4">
                  {selectedSession.transcript.map((msg: Message, idx: number) => (
                    <div key={idx} className={`${msg.role === 'user' ? 'text-blue-900' : 'text-gray-900'}`}>
                      <div className="font-medium">{msg.role === 'user' ? 'You' : 'Tally'}:</div>
                      <div className="ml-4">{msg.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Workspace Items */}
              {selectedSession.workspaceItems && selectedSession.workspaceItems.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Workspace Items</h4>
                  <div className="space-y-4">
                    {selectedSession.workspaceItems.map((card: WorkspaceCard, idx: number) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="text-sm font-medium text-gray-600 mb-2">
                          {card.type.charAt(0).toUpperCase() + card.type.slice(1)}
                        </div>
                        {card.content && (
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown>{card.content}</ReactMarkdown>
                          </div>
                        )}
                        {card.type === 'vocabulary' && card.data && (
                          <div>
                            <div className="font-medium">{card.data.word}</div>
                            <div className="text-sm text-gray-600 mt-1">{card.data.definition}</div>
                            {card.data.examples && (
                              <div className="text-sm mt-2">
                                <div className="font-medium">Examples:</div>
                                <ul className="list-disc list-inside">
                                  {card.data.examples.map((ex: string, i: number) => (
                                    <li key={i}>{ex}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Session Summary */}
              {(selectedSession as any).sessionSummary && (
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Session Analysis</h4>
                  <div className="border rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm text-gray-700">{(selectedSession as any).sessionSummary.summary}</p>
                    </div>
                    
                    {(selectedSession as any).sessionSummary.keyTopics && (
                      <div>
                        <span className="text-sm font-medium">Topics Covered:</span>
                        <div className="flex gap-2 flex-wrap mt-1">
                          {(selectedSession as any).sessionSummary.keyTopics.map((topic: string, i: number) => (
                            <span key={i} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {(selectedSession as any).sessionSummary.followUpSuggestions && (
                      <div>
                        <span className="text-sm font-medium">Recommended Next Steps:</span>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          {(selectedSession as any).sessionSummary.followUpSuggestions.map((suggestion: string, i: number) => (
                            <li key={i} className="text-sm text-gray-700">{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Skill Assessment */}
              {selectedSession.skillAssessment && (
                <div className="mb-6">
                  <h4 className="font-medium mb-3">Skill Assessment</h4>
                  <div className="border rounded-lg p-4">
                    <p className="mb-4 italic">{selectedSession.skillAssessment.motivational_statement}</p>
                    
                    <div className="mb-4">
                      <div className="font-medium mb-2">Scores:</div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(selectedSession.skillAssessment.category_scores).map(([category, data]: [string, any]) => (
                          <div key={category} className="flex justify-between">
                            <span>{category}:</span>
                            <span className={data.assessed ? 'font-medium' : 'text-gray-400'}>
                              {data.assessed ? `${data.score}/10` : 'Not assessed'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedSession.skillAssessment.skills_feedback && (
                      <div>
                        <div className="font-medium mb-2">Feedback:</div>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-green-700">Strengths:</span>
                            <ul className="list-disc list-inside ml-2">
                              {selectedSession.skillAssessment.skills_feedback.strengths.map((s: any, i: number) => (
                                <li key={i}>
                                  <span className="font-medium">{s.skill_name}:</span> {s.description}
                                  {s.example && <div className="ml-4 text-gray-600 italic">"{s.example}"</div>}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {selectedSession.skillAssessment.skills_feedback.improvement && (
                            <div>
                              <span className="font-medium text-orange-700">Area for improvement:</span>
                              <div className="ml-2">
                                <span className="font-medium">{selectedSession.skillAssessment.skills_feedback.improvement.skill_name}:</span> {selectedSession.skillAssessment.skills_feedback.improvement.description}
                                <div className="text-gray-600 italic">{selectedSession.skillAssessment.skills_feedback.improvement.suggestion}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationHistory;